import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase, isSupabaseConfigured, setRememberMePreference } from '../services/supabaseClient';
import {
    clearUserCache,
    flushPendingWrites,
    getStorageUser,
    hasUnsyncedWrites,
    hydrateFromCloud,
    setStorageUser
} from '../services/userStorage';
import { clearDeliveryStateCache } from '../services/verseDeliveryService';

const AuthContext = createContext(null);
const STARTUP_DEADLINE_MS = 12000;
const SLOW_START_NOTICE_MS = 3000;
// Admin status is a UI affordance only — every admin API re-checks the
// allowlist server-side. It resolves on its own timeline after sign-in rather
// than inside the startup deadline, so a slow /api/admin/check can never veto a
// valid session. The deadline here only stops `adminLoading` hanging forever.
const ADMIN_CHECK_DEADLINE_MS = 15000;
const RETURN_TO_KEY = 'prayer-guide-auth-return-to-v1';

const withDeadline = (promise, milliseconds, message) => new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), milliseconds);
    Promise.resolve(promise).then(
        (value) => { window.clearTimeout(timer); resolve(value); },
        (error) => { window.clearTimeout(timer); reject(error); }
    );
});

const ANONYMOUS_AUTH = {
    configured: false,
    loading: false,
    user: null,
    session: null,
    profile: null,
    entitlement: null,
    plan: 'free',
    featureCodes: [],
    licenseStatus: 'unavailable',
    accessAllowed: false,
    isAdmin: false,
    adminLoading: false,
    startupSlow: false,
    startupIssue: '',
    hasProtectedUnsyncedChanges: false,
    fatalError: '',
    scope: 'signed-out',
    retryStartup: () => {},
    refreshEntitlement: async () => {},
    signInWithGoogle: () => Promise.reject(new Error('Auth is not available')),
    discardUnsyncedChanges: () => {},
    signOut: async () => {}
};

export const useAuth = () => useContext(AuthContext) || ANONYMOUS_AUTH;

const profileFallback = (user) => {
    const meta = user?.user_metadata || {};
    return {
        id: user?.id,
        email: user?.email || null,
        full_name: meta.full_name || meta.name || null,
        avatar_url: meta.avatar_url || meta.picture || null
    };
};

const isVerifiedGoogleUser = (user) => {
    const providers = Array.isArray(user?.app_metadata?.providers) ? user.app_metadata.providers : [];
    const hasGoogleIdentity = user?.app_metadata?.provider === 'google'
        || providers.includes('google')
        || user?.identities?.some((identity) => identity.provider === 'google');
    return Boolean(user?.email_confirmed_at && hasGoogleIdentity);
};

const loadProfile = async (user) => {
    const { data, error } = await supabase
        .from('profiles')
        .select('id,email,full_name,avatar_url,created_at')
        .eq('id', user.id)
        .maybeSingle();
    if (error) throw error;
    return data || profileFallback(user);
};

const loadEntitlement = async (userId) => {
    const { data, error } = await supabase
        .from('app_entitlements')
        .select('user_id,plan_code,status,feature_codes,starts_at,expires_at,salesforce_license_id,source_version,projected_at,updated_at')
        .eq('user_id', userId)
        .maybeSingle();
    if (error) throw new Error('Your license could not be verified safely.', { cause: error });
    return data || null;
};

const effectiveAccess = (entitlement) => {
    if (!entitlement) {
        return { plan: 'free', featureCodes: [], licenseStatus: 'missing', accessAllowed: false };
    }
    const now = Date.now();
    const starts = entitlement.starts_at ? new Date(entitlement.starts_at).getTime() : null;
    const expires = entitlement.expires_at ? new Date(entitlement.expires_at).getTime() : null;
    const activeWindow = (!starts || starts <= now) && (!expires || expires > now);
    const activeStatus = ['active', 'trial'].includes(String(entitlement.status || '').toLowerCase());
    return {
        plan: activeWindow && activeStatus ? entitlement.plan_code || 'free' : 'free',
        featureCodes: activeWindow && activeStatus && Array.isArray(entitlement.feature_codes)
            ? entitlement.feature_codes
            : [],
        licenseStatus: entitlement.status || 'missing',
        accessAllowed: activeWindow && activeStatus
    };
};

const checkAdmin = async (token) => {
    if (!token) return false;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), ADMIN_CHECK_DEADLINE_MS);
    try {
        const response = await fetch('/api/admin/check', {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal
        });
        const payload = await response.json().catch(() => ({}));
        return response.ok && payload.isAdmin === true;
    } catch (_error) {
        return false;
    } finally {
        window.clearTimeout(timer);
    }
};

export const AuthProvider = ({ children }) => {
    const [session, setSession] = useState(null);
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [entitlement, setEntitlement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scope, setScope] = useState('signed-out');
    const [isAdmin, setIsAdmin] = useState(false);
    const [adminLoading, setAdminLoading] = useState(false);
    const [startupSlow, setStartupSlow] = useState(false);
    const [startupIssue, setStartupIssue] = useState('');
    const [fatalError, setFatalError] = useState('');
    const [unsyncedOwnerId, setUnsyncedOwnerId] = useState(null);
    const [retryVersion, setRetryVersion] = useState(0);

    useEffect(() => {
        if (!isSupabaseConfigured) {
            clearUserCache(getStorageUser());
            setStorageUser(null);
            setLoading(false);
            return undefined;
        }

        let active = true;
        let applyVersion = 0;
        let slowTimer;

        const beginLoading = () => {
            window.clearTimeout(slowTimer);
            setLoading(true);
            setStartupSlow(false);
            setFatalError('');
            slowTimer = window.setTimeout(() => {
                if (active) setStartupSlow(true);
            }, SLOW_START_NOTICE_MS);
        };

        const applySession = async (nextSession) => {
            const version = ++applyVersion;
            const nextUser = nextSession?.user || null;
            beginLoading();
            setSession(nextSession || null);
            setProfile(null);
            setEntitlement(null);
            setStartupIssue('');
            setIsAdmin(false);
            setAdminLoading(Boolean(nextUser));

            const previousUserId = getStorageUser();
            if (!nextUser) {
                clearDeliveryStateCache();
                if (previousUserId && hasUnsyncedWrites(previousUserId)) {
                    setUnsyncedOwnerId(previousUserId);
                    setStartupIssue('Your session ended before recent changes synced. Sign back in with the same Google account to retry, or explicitly discard them below.');
                } else {
                    if (previousUserId) clearUserCache(previousUserId);
                    setStorageUser(null);
                    setUnsyncedOwnerId(null);
                }
                window.clearTimeout(slowTimer);
                if (!active || version !== applyVersion) return;
                setUser(null);
                setScope('signed-out');
                setAdminLoading(false);
                setStartupSlow(false);
                setLoading(false);
                return;
            }

            if (previousUserId && previousUserId !== nextUser.id) {
                clearDeliveryStateCache();
                if (hasUnsyncedWrites(previousUserId)) {
                    setUnsyncedOwnerId(previousUserId);
                    setSession(null);
                    setUser(null);
                    setScope('signed-out');
                    setAdminLoading(false);
                    setStartupSlow(false);
                    setStartupIssue('Recent changes belong to a different account and have not synced. Sign back in with the same Google account, or explicitly discard them before switching accounts.');
                    window.clearTimeout(slowTimer);
                    await supabase.auth.signOut().catch(() => undefined);
                    setLoading(false);
                    return;
                }
                clearUserCache(previousUserId);
                setUnsyncedOwnerId(null);
            }
            if (!isVerifiedGoogleUser(nextUser)) {
                clearDeliveryStateCache();
                const cacheUserId = previousUserId || nextUser.id;
                if (hasUnsyncedWrites(cacheUserId)) {
                    setUnsyncedOwnerId(cacheUserId);
                } else {
                    clearUserCache(cacheUserId);
                    setStorageUser(null);
                    setUnsyncedOwnerId(null);
                }
                window.clearTimeout(slowTimer);
                setUser(nextUser);
                setScope('blocked');
                setAdminLoading(false);
                setStartupSlow(false);
                setFatalError('Prayer Guide requires a verified Google account.');
                setLoading(false);
                return;
            }
            try {
                if (previousUserId === nextUser.id) {
                    await withDeadline(
                        flushPendingWrites(nextUser.id),
                        4000,
                        'Recent changes could not be synced before refreshing your session.'
                    );
                }
                const [loadedProfile, loadedEntitlement] = await withDeadline(
                    Promise.all([
                        loadProfile(nextUser),
                        loadEntitlement(nextUser.id)
                    ]),
                    STARTUP_DEADLINE_MS,
                    'Your account and license could not be verified in time.'
                );
                if (!active || version !== applyVersion) return;

                const loadedAccess = effectiveAccess(loadedEntitlement);
                if (!loadedAccess.accessAllowed) {
                    clearDeliveryStateCache();
                    if (hasUnsyncedWrites(nextUser.id)) {
                        setUnsyncedOwnerId(nextUser.id);
                        setStartupIssue('App access is locked, and recent changes have not synced. Sign out only after reviewing the unsaved-change warning.');
                    } else {
                        clearUserCache(nextUser.id);
                        setStorageUser(null);
                        setUnsyncedOwnerId(null);
                    }
                    window.clearTimeout(slowTimer);
                    setUser(nextUser);
                    setProfile(loadedProfile);
                    setEntitlement(loadedEntitlement);
                    setScope('license-blocked');
                    setStartupSlow(false);
                    setLoading(false);
                    return;
                }

                setStorageUser(nextUser.id);
                await withDeadline(
                    hydrateFromCloud(nextUser.id),
                    STARTUP_DEADLINE_MS,
                    'Your private app data could not be loaded in time.'
                );
                if (!active || version !== applyVersion) return;

                window.clearTimeout(slowTimer);
                setUser(nextUser);
                setUnsyncedOwnerId(null);
                setProfile(loadedProfile);
                setEntitlement(loadedEntitlement);
                setScope(nextUser.id);
                setStartupSlow(false);
                setLoading(false);
            } catch (error) {
                if (!active || version !== applyVersion) return;
                window.clearTimeout(slowTimer);
                // Preserve an active user's in-memory cache when the failure is
                // an unsynced write. Retry/sign-out can then explicitly resolve
                // it instead of silently discarding data that looked saved.
                if (!hasUnsyncedWrites(nextUser.id)) {
                    clearUserCache(nextUser.id);
                    setStorageUser(null);
                    setUnsyncedOwnerId(null);
                } else {
                    setUnsyncedOwnerId(nextUser.id);
                }
                setUser(nextUser);
                setScope('blocked');
                setAdminLoading(false);
                setStartupSlow(false);
                setFatalError(error.message || 'Your account data could not be loaded safely.');
                setLoading(false);
            }
        };

        const { data: authListener } = supabase.auth.onAuthStateChange((event, nextSession) => {
            if (event === 'INITIAL_SESSION') return;
            // Avoid issuing Supabase queries inside the auth callback itself.
            window.setTimeout(() => applySession(nextSession), 0);
        });

        supabase.auth.getSession()
            .then(({ data, error }) => {
                if (error) throw error;
                return applySession(data.session);
            })
            .catch((error) => {
                if (!active) return;
                window.clearTimeout(slowTimer);
                const previousUserId = getStorageUser();
                if (previousUserId && hasUnsyncedWrites(previousUserId)) {
                    setUnsyncedOwnerId(previousUserId);
                    setStartupIssue('Your session could not be restored before recent changes synced. Sign back in with the same Google account to retry, or explicitly discard them below.');
                } else {
                    if (previousUserId) clearUserCache(previousUserId);
                    setStorageUser(null);
                    setUnsyncedOwnerId(null);
                }
                setSession(null);
                setUser(null);
                setScope('signed-out');
                setStartupSlow(false);
                setFatalError(previousUserId && hasUnsyncedWrites(previousUserId)
                    ? ''
                    : (error.message || 'Your sign-in session could not be restored.'));
                setLoading(false);
            });

        return () => {
            active = false;
            window.clearTimeout(slowTimer);
            authListener.subscription.unsubscribe();
        };
    }, [retryVersion]);

    // Admin status resolves after the session is already live. Consumers read
    // `adminLoading` while it settles, so a slow or unreachable admin endpoint
    // costs an admin badge, never the sign-in itself.
    const accessToken = session?.access_token || null;
    const userId = user?.id || null;
    useEffect(() => {
        if (!isSupabaseConfigured || !userId || !accessToken) {
            setIsAdmin(false);
            setAdminLoading(false);
            return undefined;
        }
        let active = true;
        setAdminLoading(true);
        checkAdmin(accessToken).then((allowed) => {
            if (!active) return;
            setIsAdmin(allowed);
            setAdminLoading(false);
        });
        return () => { active = false; };
    }, [userId, accessToken]);

    useEffect(() => {
        if (!isSupabaseConfigured || !user?.id) return undefined;
        let active = true;
        let checking = false;

        const recheckLicense = async () => {
            if (checking || !active || document.visibilityState === 'hidden') return;
            checking = true;
            try {
                const nextEntitlement = await loadEntitlement(user.id);
                if (!active) return;
                const nextAccess = effectiveAccess(nextEntitlement);
                setEntitlement(nextEntitlement);
                setStartupIssue('');
                if (!nextAccess.accessAllowed) {
                    clearDeliveryStateCache();
                    if (hasUnsyncedWrites(user.id)) {
                        setUnsyncedOwnerId(user.id);
                        setStartupIssue('App access is locked, and recent changes have not synced. Sign out only after reviewing the unsaved-change warning.');
                    } else {
                        clearUserCache(user.id);
                        setStorageUser(null);
                        setUnsyncedOwnerId(null);
                    }
                    setScope('license-blocked');
                } else if (scope === 'license-blocked') {
                    setRetryVersion((value) => value + 1);
                }
            } catch (_error) {
                if (active) {
                    setStartupIssue('Your license could not be refreshed. Server actions remain locked to verified access.');
                }
            } finally {
                checking = false;
            }
        };

        const interval = window.setInterval(recheckLicense, 60 * 1000);
        const onVisibility = () => {
            if (document.visibilityState === 'visible') recheckLicense();
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            active = false;
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [scope, user?.id]);

    useEffect(() => {
        if (!user?.id || scope !== user.id) return undefined;
        const warnBeforeUnload = (event) => {
            if (!hasUnsyncedWrites(user.id)) return;
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warnBeforeUnload);
        return () => window.removeEventListener('beforeunload', warnBeforeUnload);
    }, [scope, user?.id]);

    const signInWithGoogle = async ({ rememberMe = true, returnTo = '/' } = {}) => {
        if (!isSupabaseConfigured) return Promise.reject(new Error('Supabase is not configured'));
        setRememberMePreference(rememberMe);
        const safeReturnTo = typeof returnTo === 'string' && returnTo.startsWith('/') && !returnTo.startsWith('//')
            ? returnTo
            : '/';
        window.sessionStorage.setItem(RETURN_TO_KEY, safeReturnTo);
        const { data, error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: { redirectTo: `${window.location.origin}/login` }
        });
        if (error) throw error;
        return data;
    };

    const signOut = async () => {
        const userId = getStorageUser();
        if (userId) {
            try {
                await withDeadline(flushPendingWrites(userId), 4000, 'Pending changes are still syncing.');
            } catch (error) {
                setStartupIssue(error.message || 'Recent changes have not been saved yet.');
                const discard = window.confirm(
                    'Some recent changes could not be saved to your account. Sign out anyway and discard those unsaved changes?'
                );
                if (!discard) return false;
            }
        }
        await supabase.auth.signOut();
        if (userId) clearUserCache(userId);
        clearDeliveryStateCache();
        setStorageUser(null);
        window.sessionStorage.removeItem(RETURN_TO_KEY);
        setUnsyncedOwnerId(null);
        return true;
    };

    const discardUnsyncedChanges = () => {
        if (!unsyncedOwnerId) return false;
        const confirmed = window.confirm('Discard the recent changes that could not be saved? This cannot be undone.');
        if (!confirmed) return false;
        clearUserCache(unsyncedOwnerId);
        setStorageUser(null);
        setUnsyncedOwnerId(null);
        setStartupIssue('');
        return true;
    };

    const refreshEntitlement = async () => {
        if (!user) return;
        try {
            const result = await loadEntitlement(user.id);
            setEntitlement(result);
            setStartupIssue('');
            if (effectiveAccess(result).accessAllowed && scope === 'license-blocked') {
                setRetryVersion((value) => value + 1);
            }
        } catch (error) {
            setFatalError(error.message || 'Your license could not be verified safely.');
        }
    };

    const access = useMemo(() => effectiveAccess(entitlement), [entitlement]);
    const value = {
        configured: isSupabaseConfigured,
        loading,
        session,
        user,
        profile,
        entitlement,
        ...access,
        isAdmin,
        adminLoading,
        startupSlow,
        startupIssue,
        hasProtectedUnsyncedChanges: Boolean(unsyncedOwnerId),
        fatalError,
        scope,
        retryStartup: () => setRetryVersion((value) => value + 1),
        refreshEntitlement,
        signInWithGoogle,
        discardUnsyncedChanges,
        signOut
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { RETURN_TO_KEY };
export default AuthContext;
